import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { useEffect, useCallback, lazy, Suspense } from "react";
import { sfxTap, sfxLinkTap, sfxTabTap, sfxMenuTap, sfxInputTap } from "@/lib/sfx";
import Index from "./pages/Index";
import CookieConsent from "@/components/CookieConsent";
import PointsRewardAnimation from "@/components/PointsRewardAnimation";
import RankUpCelebration from "@/components/RankUpCelebration";
import QuestCompleteCelebration from "@/components/QuestCompleteCelebration";
import { trackPageView } from "@/lib/analytics";

const Profile = lazy(() => import("./pages/Profile"));
const Upload = lazy(() => import("./pages/Upload"));
const Auth = lazy(() => import("./pages/Auth"));
const ArtistDetail = lazy(() => import("./pages/ArtistDetail"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const TrendingArtists = lazy(() => import("./pages/TrendingArtists"));
const RepublicPoints = lazy(() => import("./pages/RepublicPoints"));
const SearchPage = lazy(() => import("./pages/Search"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CopyrightPolicy = lazy(() => import("./pages/CopyrightPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { requestAnimationFrame(() => window.scrollTo(0, 0)); }, [pathname]);
  return null;
};

const PageViewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView();
  }, [location.pathname]);
  return null;
};

const GlobalClickSfx = () => {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("[role='tab']")) {
        sfxTabTap();
      } else if (target.closest("[role='menuitem'], [role='option']")) {
        sfxMenuTap();
      } else if (target.closest("a")) {
        sfxLinkTap();
      } else if (target.closest("input, textarea, select")) {
        sfxInputTap();
      } else if (target.closest("button, [role='button']")) {
        sfxTap();
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <PageViewTracker />
          <GlobalClickSfx />
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/:userId" element={<Profile />} />
            <Route path="/upload" element={<Upload />} />
            <Route path="/artists" element={<TrendingArtists />} />
            <Route path="/artist/:id" element={<ArtistDetail />} />
            <Route path="/event/:id" element={<EventDetail />} />
            <Route path="/points" element={<RepublicPoints />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/copyright" element={<CopyrightPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <CookieConsent />
          <PointsRewardAnimation />
          <RankUpCelebration />
          <QuestCompleteCelebration />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
